import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import { orderBy, filter } from 'lodash'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { formatCnpj } from '@core/utils/utils'
import Fuse from 'fuse.js'

export default function usePhysicianList() {
  // Use toast
  const toast = useToast()

  const refPhysicianListTable = ref(null)

  const getPhysicianStatus = status => {
    if (status === 'denied') {
      return 'Recusado'
    }
    return status === 'approved' ? 'Aprovado' : 'Pendente'
  }

  const resolveStatusVariant = status => {
    if (status === 'approved') return 'success'
    if (status === 'pending') return 'secondary'
    return 'danger'
  }

  const getStatusLabelModal = status => {
    if (status === 'approved') return 'Aprovar'
    if (status === 'denied') return 'Recusar'
    return 'Pendente'
  }

  function filterEntries(options, search) {
    const fuse = new Fuse(options, {
      keys: [
        'physician.name',
        'status_label',
        'motive',
        'physician_company.name',
      ],
      shouldSort: true,
    })
    return search.length
      ? fuse.search(search).map(({ item }) => item)
      : fuse.list
  }

  // Table Handlers
  const tableColumns = [
    {
      key: 'physician_company.cnpj', label: 'CNPJ', sortable: true, formatter: formatCnpj,
    },
    { key: 'physician_company.name', label: 'Empresa', sortable: true },
    { key: 'physician.name', label: 'Profissional', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
    },
    { key: 'motive', label: 'Motivo', sortable: true },
    { key: 'contract_files', label: 'Contratos', formatter: files => (files.length ? `${files.length}` : '') },
    { key: 'actions', label: 'Ações' },
  ]

  const perPage = ref(10)
  const totalPhysicians = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('physician_company.name')
  const isSortDirDesc = ref(true)
  const institutionFilter = ref(null)
  const workplaceIdFilter = ref(null)
  const statusFilter = ref(null)
  const statusOptions = [
    { label: 'Ativo', value: 'active' },
    { label: 'Aprovado', value: 'approved' },
    { label: 'Pendente', value: 'pending' },
    { label: 'Recusado', value: 'denied' },
  ]

  const dataMeta = computed(() => {
    const localItemsCount = refPhysicianListTable.value ? refPhysicianListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalPhysicians.value,
    }
  })

  const refetchData = () => {
    if (refPhysicianListTable.value) refPhysicianListTable.value.refresh()
  }

  watch([currentPage, perPage, searchQuery, workplaceIdFilter, statusFilter], () => {
    refetchData()
  })
  const getListParams = () => ({
    q: searchQuery.value,
    perPage: perPage.value,
    page: currentPage.value,
    sortBy: sortBy.value,
    sortDesc: isSortDirDesc.value ? 'desc' : 'asc',
    sortDirection: isSortDirDesc.value ? 'desc' : 'asc',
    workplace_id: workplaceIdFilter.value,
    status: statusFilter.value ? statusFilter.value.value : null,
  })

  const fetchPhysicians = (ctx, callback) => {
    const params = getListParams()
    if (!params.workplace_id) {
      callback([])
      return
    }

    store
      .dispatch('app-institution/fetchPhysiciansPj', params)
      .then(response => {
        let physicians = orderBy(response.data.data, [params.sortBy], [params.sortDirection])
        physicians = physicians.map(p => ({
          ...p,
          status_label: getPhysicianStatus(p.status),
        }))

        if (params.status) { physicians = filter(physicians, { status: params.status }) }
        if (params.q) physicians = filterEntries(physicians, params.q)
        callback(physicians)
        totalPhysicians.value = physicians.count
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao buscar',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  return {
    fetchPhysicians,
    tableColumns,
    perPage,
    currentPage,
    totalPhysicians,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refPhysicianListTable,
    refetchData,
    // Extra Filters
    statusOptions,
    institutionFilter,
    statusFilter,
    workplaceIdFilter,
    getPhysicianStatus,
    getStatusLabelModal,
    resolveStatusVariant,
  }
}
